import React from 'react';
import ArchitectureLayout from 'components/architecture/ArchitectureLayout';

/**
 * Compute Architecture Page
 * Documents the serverless compute infrastructure
 */
const Compute = () => {
  const sections = [
    {
      id: 'overview',
      title: 'Compute Overview',
      content: `Our compute infrastructure is built on **Azure Functions** and **Azure Container Apps**, providing:

- Serverless execution model
- Automatic scaling
- Multi-region deployment
- High availability
- Cost-effective pay-per-use pricing`
    },
    {
      id: 'serverless-model',
      title: 'Serverless Architecture',
      content: `We leverage serverless computing for:

- **No infrastructure management**: Azure handles all infrastructure
- **Automatic scaling**: Scales from zero to thousands of instances
- **Cost optimization**: Pay only for execution time
- **Fast deployment**: Deploy code without managing servers
- **Built-in monitoring**: Integrated logging and metrics

\`\`\`mermaid
graph TB
    A[Request] --> B[Load Balancer]
    B --> C[Function Instance 1]
    B --> D[Function Instance 2]
    B --> E[Function Instance N]
    C --> F[Shared Resources]
    D --> F
    E --> F
    F --> G[MongoDB]
    F --> H[Storage]
    F --> I[Key Vault]
\`\`\``
    },
    {
      id: 'scaling',
      title: 'Auto-Scaling',
      content: `Our compute automatically scales based on:

- **Request volume**: More requests = more instances
- **Execution time**: Long-running functions may use dedicated instances
- **Concurrent executions**: Multiple instances handle parallel requests
- **Resource constraints**: CPU and memory limits trigger scaling

Scaling happens in seconds, ensuring responsive performance under any load.`
    },
    {
      id: 'regions',
      title: 'Multi-Region Deployment',
      content: `We deploy compute resources across multiple Azure regions:

- **Primary region**: Germany West Central (main deployment)
- **Secondary regions**: Available for disaster recovery
- **Global distribution**: Ensures low latency worldwide
- **Failover**: Automatic failover in case of regional issues`
    },
    {
      id: 'performance',
      title: 'Performance Characteristics',
      content: `Our compute infrastructure provides:

- **Cold start time**: < 2 seconds for most functions
- **Warm execution**: < 100ms response time
- **Concurrent executions**: Thousands of parallel requests
- **Throughput**: Handles millions of requests per day
- **Reliability**: 99.9% uptime SLA`
    }
  ];

  return (
    <ArchitectureLayout
      title="Compute"
      lastUpdated="2025-01-15"
      sections={sections}
    />
  );
};

export default Compute;

